import { useSuspenseQuery } from "@tanstack/react-query";
import {
  isUserMatch,
  queries,
  useAuthConfig,
  useUpdateCurrentUser,
} from "@theprelude/core";
import {
  Button,
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Form,
  InputFormField,
  UserIcon,
  notifyError,
  notifySuccess,
} from "@theprelude/ds";
import { useForm } from "react-hook-form";
import { usePopper } from "@/hooks/use-popper";
import { tb } from "@/lib/tracking";

type ProfileFormData = {
  name: string;
};

const EditProfileButton = () => {
  const authConfig = useAuthConfig();
  const dialog = usePopper();

  const userQuery = useSuspenseQuery({
    ...queries.iam.getAccount(authConfig),
    select: (account) =>
      account.users.find((u) => isUserMatch(account.whoami, u)),
  });

  const user = userQuery.data;
  if (!user) {
    return null;
  }

  return (
    <Dialog {...dialog}>
      <DialogTrigger asChild>
        <Button
          icon={<UserIcon className="text-secondary-foreground" />}
          className={tb("edit-profile")}
          intent="secondary"
        >
          Edit profile
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Profile</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <UpdateProfileForm user={user} onDone={dialog.close} />
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
};

function UpdateProfileForm({
  user,
  onDone,
}: {
  user: { name?: string };
  onDone: () => void;
}) {
  const updateCurrentUser = useUpdateCurrentUser();

  const form = useForm<ProfileFormData>({
    defaultValues: {
      name: user.name || "",
    },
  });

  const handleSubmit = (data: ProfileFormData) => {
    updateCurrentUser.mutate(
      { name: data.name },
      {
        onSuccess: () => {
          onDone();
          notifySuccess({
            message: "Profile updated successfully",
            dismissible: true,
          });
        },
        onError: (error) => {
          notifyError({
            message: "Failed to update profile",
            dismissible: true,
            error,
          });
        },
      },
    );
  };

  return (
    <Form {...form} onSubmitValid={handleSubmit}>
      <div className="flex flex-col gap-4">
        <InputFormField
          label="Name"
          placeholder="Enter your name"
          {...form.register("name")}
        />
        <div className="flex justify-end gap-2 pt-2">
          <DialogClose asChild>
            <Button intent="secondary">Cancel</Button>
          </DialogClose>
          <Button
            type="submit"
            loading={updateCurrentUser.isPending}
            disabled={updateCurrentUser.isPending}
            intent="primary"
          >
            Save Changes
          </Button>
        </div>
      </div>
    </Form>
  );
}

export default EditProfileButton;
